import { HttpClient, HttpParams } from '@angular/common/http';
import { EventEmitter, Injectable, inject } from '@angular/core';
import { apiEndpointURL } from '@app/core/constants/endpoints-url';
import { Utilities } from '@app/core/utils/utilities';
import { Observable } from 'rxjs';
import { CourseListingPageData } from '../models/course-data.model';
import { CourseType } from '@app/shared/enm/course-instance.enum';
import { CourseCommonFunctions } from '../course-util/course-common-functions';
import { StagesModules } from '@app/shared/enm/stages-modules.enum';
import { ClearingAvailablePayload, SecretTariffPayload } from '../models/clearing-data.model';

@Injectable({
  providedIn: 'root'
})
export class CourseService {
  util = inject(Utilities);
  httpClient = inject(HttpClient);
  courseCommonFn = inject(CourseCommonFunctions);
  StageModuleConstants = StagesModules;
  popUpOptionsEvent: EventEmitter<{ cause: string }> = new EventEmitter();

  getCoursesData(pageNo: number, sortBy?: string, sortOrder?: string, filterDetails?: any, erQualId?: any, pageSize?: number, pageName?: string, from?: string): Observable<CourseListingPageData> {
    const coursesDataUrl = [apiEndpointURL.getInstitutionEndpoint, JSON.parse(this.util.institutionDetails())?.institutionId, 'courses'].join('/')
    let params = new HttpParams().set('pageNumber', pageNo).set('pageSize', pageSize ? pageSize : this.util.recordsPerPage());
    if (sortBy)
      params = params.set('sortBy', sortBy);
    if (sortOrder)
      params = params.set('sortOrder', sortOrder);
    if (pageName)
      params = params.set('pageName', pageName);
    if (filterDetails)
      Object.keys(filterDetails).forEach((data) => {
        params = params.set(data, filterDetails[data])
      });
    if (erQualId)
      params = params.set('erQualificationId', erQualId);
    if (from === 'resolver') params = params.set('resolver', true);
    return this.httpClient.get<CourseListingPageData>(coursesDataUrl, { params });
  }

  getCourseInstanceData(pageNo: number, courseId: number, sortBy?: string, sortOrder?: string, instanceFilterDetails?: any, relatedCoursesFilter?: boolean, erQualId?: any, clearing?: boolean, pageSize?: any): Observable<any> {
    let params = new HttpParams().set('pageNumber', pageNo).set('pageSize', pageSize ? pageSize : this.util.recordsPerPage())
    let coursesDataUrl;

    if (clearing) {
      coursesDataUrl = [apiEndpointURL.getInstitutionEndpoint, JSON.parse(this.util.institutionDetails())?.institutionId, 'clearingCourseInstances'].join('/');
    }
    else {
      coursesDataUrl = [apiEndpointURL.getInstitutionEndpoint, JSON.parse(this.util.institutionDetails())?.institutionId, 'courses', courseId, 'courseInstances'].join('/');
      params = params.set('relatedCoursesFilter', false);
    }

    if (relatedCoursesFilter)
      params = new HttpParams().set('relatedCoursesFilter', relatedCoursesFilter);
    if (sortBy)
      params = params.set('sortBy', sortBy);
    if (sortOrder)
      params = params.set('sortOrder', sortOrder);
    if (instanceFilterDetails)
      Object.keys(instanceFilterDetails).forEach((data) => {
        params = params.set(data, instanceFilterDetails[data])
      });
    if (erQualId)
      params = params.set('erQualificationId', erQualId);
    return this.httpClient.get<any>(coursesDataUrl, { params });
  }

  updateClearingAvailable(institutionId: number, payload: ClearingAvailablePayload): Observable<any> {
    const editCourseUrl = [apiEndpointURL.getInstitutionEndpoint, institutionId, 'clearingFlag'].join('/');
    return this.httpClient.put(editCourseUrl, payload);
  }

  updateSecretUcasTariff(institutionId: number, clearingInstanceId: any, payload: SecretTariffPayload): Observable<any> {
    const editCourseUrl = [apiEndpointURL.getInstitutionEndpoint, institutionId, 'clearingCourseInstances', clearingInstanceId].join('/');
    return this.httpClient.put(editCourseUrl, payload);
  }

  archiveOrUnarchiveCourse(courseId: any, status: string): Observable<any> {
    const url = [apiEndpointURL.getInstitutionEndpoint, JSON.parse(this.util.institutionDetails())?.institutionId, "courses", courseId, status].join('/');
    let bodyContent: any = null;
    if (this.util.getStoredUserDetails().userId)
      bodyContent = { updatedBy: Number(this.util.getStoredUserDetails().userId) };
    return this.httpClient.patch(url, bodyContent);
  }

  archiveOrUnarchiveCourseInstance(courseId: any, status: string, courseInstanceId: any): Observable<any> {
    const url = [apiEndpointURL.getInstitutionEndpoint, JSON.parse(this.util.institutionDetails())?.institutionId, "courses", courseId, 'courseInstances', courseInstanceId, status].join('/');
    let bodyContent: any = null;
    if (this.util.getStoredUserDetails().userId)
      bodyContent = { updatedBy: Number(this.util.getStoredUserDetails().userId) };
    return this.httpClient.patch(url, bodyContent);
  }

  getCourseJSON(courseTypeId: any): Observable<any> {
    const params = new HttpParams().set('headerNotRequired', true);
    let url = apiEndpointURL.courseFieldJson;
    if (courseTypeId === CourseType.GeneralCourseTypeId)
      url = apiEndpointURL.generalCourseFieldJson;
    return this.httpClient.get(url, { params });
  }

  getCourseReferenceData() {
    return this.httpClient.get(apiEndpointURL.courseReferenceEndpoint);
  }

  getCourseInstanceJSON(courseTypeId: any): Observable<any> {
    const params = new HttpParams().set('headerNotRequired', true);
    let url = apiEndpointURL.courseInstanceFieldJson;
    if (courseTypeId === CourseType.GeneralCourseTypeId)
      url = apiEndpointURL.generalCourseInstanceFieldJson;
    return this.httpClient.get(url, { params });
  }

  getInstanceReferenceData(paramValue?: any, from?: string) {
    let params = new HttpParams({});
    if (paramValue)
      Object.keys(paramValue).forEach((key) => {
        params = params.set(key, paramValue[key])
      })
    if (from === 'resolver') params = params.set('resolver', 'true');
    else if (paramValue) params = params.set('hideLoader', 'true');
    return this.httpClient.get(apiEndpointURL.instanceReferenceEndpoint, { params });
  }

  saveCourseInstanceData(institutionId: any, courseInstanceData: any): Observable<any> {
    const saveUrl = apiEndpointURL.getInstitutionEndpoint + '/' + institutionId + '/courses';
    return this.httpClient.post(saveUrl, courseInstanceData);
  }

  editCourseData(institutionId: any, courseId: any, courseData: any): Observable<any> {
    const editCourseUrl = apiEndpointURL.getInstitutionEndpoint + '/' + institutionId + '/courses/' + courseId;
    return this.httpClient.put(editCourseUrl, courseData);
  }

  editCourseInstanceData(institutionId: any, courseId: any, instanceId: any, courseInstanceData: any): Observable<any> {
    const saveUrl = apiEndpointURL.getInstitutionEndpoint + '/' + institutionId + '/courses/' + courseId + '/courseInstances/' + instanceId;
    return this.httpClient.put(saveUrl, courseInstanceData);
  }

  saveInstanceData(institutionId: any, courseId: any, courseInstanceData: any): Observable<any> {
    const saveUrl = apiEndpointURL.getInstitutionEndpoint + '/' + institutionId + '/courses/' + courseId + '/courseInstances';
    return this.httpClient.post(saveUrl, courseInstanceData);
  }

  getViewCourseDetails(institutionId: any, courseId: any, from?: any) {
    let params = new HttpParams();
    if (from === 'resolver') params = params.set('resolver', 'true');
    const viewCourseUrl = [apiEndpointURL.getInstitutionEndpoint, institutionId, 'courses', courseId].join('/');
    return this.httpClient.get(viewCourseUrl, { params });
  }

  getViewCourseInstanceDetails(institutionId: any, courseId: any, instanceId: any, from?: string) {
    let params = new HttpParams();
    if (from === 'resolver') params = params.set('resolver', 'true');
    const viewCourseUrl = [apiEndpointURL.getInstitutionEndpoint, institutionId, 'courses', courseId, 'courseInstances', instanceId].join('/');
    return this.httpClient.get(viewCourseUrl, { params });
  }

  getCourseAjaxReferenceData(filterBy: string, status?: string) {
    let params = new HttpParams().set("filterBy", filterBy);
    if (status)
      params = params.set('status', status);
    const url = [apiEndpointURL.getInstitutionEndpoint, JSON.parse(this.util.institutionDetails())?.institutionId, "courses/filterData"].join('/');
    return this.httpClient.get(url, { params });
  }

  getInstanceAjaxReferenceData(courseId: any, filterBy: string, clearing: boolean) {
    const params = new HttpParams().set("filterBy", filterBy);
    let url;

    if (clearing)
      url = [apiEndpointURL.getInstitutionEndpoint, JSON.parse(this.util.institutionDetails())?.institutionId, 'clearingCourseInstances/filterData'].join('/');
    else
      url = [apiEndpointURL.getInstitutionEndpoint, JSON.parse(this.util.institutionDetails())?.institutionId, "courses", courseId, "courseInstances/filterData"].join('/');
    return this.httpClient.get(url, { params });
  }

  getRelatedCourseList(institutionId: any, courseId: any, instanceId: any, pageNumber: number, pageSize: number) {
    const getRelatedCourseUrl = [apiEndpointURL.getInstitutionEndpoint, institutionId, 'courses', courseId, 'courseInstances', instanceId, 'relatedCourseInstances'].join('/');
    const params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize);
    return this.httpClient.get(getRelatedCourseUrl, { params });
  }

  getRelatedCourseJSONDetails() {
    const params = new HttpParams().set('headerNotRequired', true);
    return this.httpClient.get(apiEndpointURL.relatedCourseFieldJson, { params });
  }

  getRelatedCourseRefData() {
    return this.httpClient.get(apiEndpointURL.relatedCourseReferenceEndpoint);
  }

  saveRelatedCourseData(institutionId: any, courseId: any, instanceId: any, relatedCourseData: any): Observable<any> {
    const saveRelatedCourseUrl = [apiEndpointURL.getInstitutionEndpoint, institutionId, 'courses', courseId, 'courseInstances', instanceId, 'relatedCourseInstances'].join('/');
    return this.httpClient.post(saveRelatedCourseUrl, relatedCourseData);
  }

  editRelatedCourseData(institutionId: any, courseId: any, instanceId: any, pathwayCourseRelationshipId: any, relatedCourseData: any): Observable<any> {
    const saveRelatedCourseUrl = [apiEndpointURL.getInstitutionEndpoint, institutionId, 'courses', courseId, 'courseInstances', instanceId, 'relatedCourseInstances', pathwayCourseRelationshipId].join('/');
    return this.httpClient.put(saveRelatedCourseUrl, relatedCourseData);
  }

  viewRelatedCourseData(institutionId: any, courseId: any, instanceId: any, pathwayRelationshipId: any): Observable<any> {
    const viewRelatedCourseUrl = [apiEndpointURL.getInstitutionEndpoint, institutionId, 'courses', courseId, 'courseInstances', instanceId, 'relatedCourseInstances', pathwayRelationshipId].join('/');
    return this.httpClient.get(viewRelatedCourseUrl);
  }

  linkOrUnlinkRelatedCourse(institutionId: any, courseId: any, instanceId: any, pathwayRelationshipId: any, status: string): Observable<any> {
    const updateRelatedCourseStatus = [apiEndpointURL.getInstitutionEndpoint, institutionId, 'courses', courseId, 'courseInstances', instanceId, 'relatedCourseInstances', pathwayRelationshipId, status].join('/');
    const bodyContent: any = {};
    this.courseCommonFn.getUserData(bodyContent);
    return this.httpClient.patch(updateRelatedCourseStatus, bodyContent);
  }

  getCourseGroupTypeJSONDetails() {
    const params = new HttpParams().set('headerNotRequired', true);
    return this.httpClient.get(apiEndpointURL.courseGroupTypeFieldJson, { params });
  }

  getCourseGroupReferenceData() {
    return this.httpClient.get(apiEndpointURL.courseGroupTypeReferenceEndpoint);
  }

  saveCourseGroupDetails(courseGroupData: any, institutionId: any, courseId: any, courseInstanceId: any, pageType: any) {
    const saveCourseGroupTypeUrl = [apiEndpointURL.getInstitutionEndpoint, institutionId, 'courses', courseId, 'courseInstances', courseInstanceId, 'courseGroups'].join('/');
    if (pageType === 'edit')
      return this.httpClient.put(saveCourseGroupTypeUrl, courseGroupData);
    else
      return this.httpClient.post(saveCourseGroupTypeUrl, courseGroupData);
  }
}
